/**
 * OnboardingTour — a first-run guided walkthrough that surfaces SlideCon's
 * highest-value actions in 4 tooltips. Zero external deps: we position a
 * portal-rendered card next to a target element using its bounding rect.
 *
 * Trigger: appears once per user (localStorage flag) on the /dashboard route,
 * ~1200 ms after mount so anchor elements have laid out.
 * Skip: dismisses and marks the tour complete.
 * Restart: exported hook `useRestartOnboardingTour()` lets a Profile menu
 * item wipe the flag and re-launch.
 */
import { useEffect, useLayoutEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, X, Sparkles, CalendarClock, Presentation, Bell } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY_PREFIX = "slidecon_onboarding_tour_v1_";
const HIDDEN_ROUTES = ["/present/", "/stream/", "/presenter/", "/setlist/", "/onboarding", "/login", "/signup"];
const DEFAULT_ANCHOR_MARGIN = 12;

// Fire-and-forget analytics — never blocks the UI, never throws.
function track(token, event, properties = {}) {
  if (!token) return;
  try {
    fetch(`${API_URL}/api/analytics/event`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ event, properties }),
      keepalive: true,
    }).catch(() => {});
  } catch { /* noop */ }
}

/** Steps to walk the user through. Each anchors on a data-testid — if the
 *  element isn't in the DOM we auto-skip that step. */
const STEPS = [
  {
    id: "quick-actions",
    testId: "quick-actions-grid",
    Icon: Sparkles,
    title: "Start with a quick action",
    body: "Upload a PDF, generate slides from a Bible verse, or paste a sermon outline — SlideCon builds a ready-to-preach deck in seconds.",
    placement: "bottom",
  },
  {
    id: "preachkit",
    testId: "feature-preach-kit",
    Icon: Presentation,
    title: "PreachKit · your AI study desk",
    body: "Outline a sermon, look up scripture, cross-reference verses, and pull ready-made illustrations — all in one place.",
    placement: "top",
  },
  {
    id: "community",
    testId: "feature-community",
    Icon: CalendarClock,
    title: "Plan and share with your community",
    body: "Schedule services, assign volunteers, share sermon files, and get automatic email reminders 24 hours before every gathering.",
    placement: "top",
  },
  {
    id: "profile-menu",
    testId: "user-menu-trigger",
    Icon: Bell,
    title: "Tune your notifications",
    body: "Open Profile → Notification preferences to enable a weekly digest of what's ahead — perfect for busy pastors and worship leaders.",
    placement: "bottom",
  },
];

function computePosition(rect, placement) {
  const cardW = 320;
  const cardH = 148;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const margin = DEFAULT_ANCHOR_MARGIN;

  let top = 0, left = 0, arrow = "top";
  const centerX = rect.left + rect.width / 2 - cardW / 2;
  const clampedLeft = Math.max(12, Math.min(centerX, vw - cardW - 12));

  if (placement === "top") {
    top = rect.top - cardH - margin;
    left = clampedLeft;
    arrow = "bottom";
    if (top < 12) { top = rect.bottom + margin; arrow = "top"; }
  } else {
    top = rect.bottom + margin;
    left = clampedLeft;
    arrow = "top";
    if (top + cardH > vh - 12) { top = rect.top - cardH - margin; arrow = "bottom"; }
  }
  // Final vertical clamp — never let the card go above viewport or below it.
  top = Math.max(12, Math.min(top, vh - cardH - 12));
  return { top, left, arrow, width: cardW };
}

function keyFor(userId) {
  return `${STORAGE_KEY_PREFIX}${userId || "guest"}`;
}

export function useRestartOnboardingTour() {
  const { user } = useAuth();
  return useCallback(() => {
    try { localStorage.removeItem(keyFor(user?.user_id)); } catch { /* noop */ }
    // Force reload of Dashboard so the tour mounts fresh
    window.location.href = "/dashboard?tour=1";
  }, [user?.user_id]);
}

export default function OnboardingTour() {
  const { user, isAuthenticated, token } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [active, setActive] = useState(false);
  const [step, setStep] = useState(0);
  const [pos, setPos] = useState(null);
  const anchorRef = useRef(null);
  const startedRef = useRef(false);
  const finishedRef = useRef(false);

  // Decide when to start
  useEffect(() => {
    if (!isAuthenticated || !user?.user_id) return;
    if (location.pathname !== "/dashboard") return;
    if (HIDDEN_ROUTES.some((r) => location.pathname.startsWith(r))) return;

    const params = new URLSearchParams(location.search);
    const forced = params.get("tour") === "1";
    let done = false;
    try { done = !!localStorage.getItem(keyFor(user.user_id)); } catch { /* ignore */ }
    if (done && !forced) return;

    const t = setTimeout(() => {
      setActive(true);
      if (!startedRef.current) {
        startedRef.current = true;
        track(token, "tour_started", { forced });
      }
    }, 1200);
    return () => clearTimeout(t);
  }, [isAuthenticated, user?.user_id, location.pathname, location.search, token]);

  // Find the next available step's anchor, skipping missing ones.
  const seekViable = useCallback((from) => {
    for (let i = from; i < STEPS.length; i++) {
      const el = document.querySelector(`[data-testid="${STEPS[i].testId}"]`);
      if (el) return { i, el };
    }
    return null;
  }, []);

  // Position the card once, then again on resize/scroll
  useLayoutEffect(() => {
    if (!active) return;
    const found = seekViable(step);
    if (!found) { finish(); return; }
    if (found.i !== step) setStep(found.i);
    anchorRef.current = found.el;
    // Softly scroll the anchor into view
    try { found.el.scrollIntoView({ behavior: "smooth", block: "center" }); } catch { /* ignore */ }

    const reposition = () => {
      const el = anchorRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setPos(computePosition(rect, STEPS[found.i].placement));
    };
    // Give the smooth-scroll a beat to settle
    const t = setTimeout(reposition, 220);
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [active, step]);

  const finish = useCallback((reason = "completed") => {
    setActive(false);
    setPos(null);
    if (!finishedRef.current) {
      finishedRef.current = true;
      // For a "completed" finish (Done on final step), also emit the
      // step_completed event so the funnel accurately reflects step 4.
      if (reason === "completed") {
        track(token, `tour_step_completed_${step + 1}`, { step: step + 1, total_steps: STEPS.length });
      }
      const event = reason === "skipped" ? "tour_skipped" : "tour_finished";
      track(token, event, { last_step: step + 1, total_steps: STEPS.length });
    }
    try { localStorage.setItem(keyFor(user?.user_id), new Date().toISOString()); } catch { /* ignore */ }
    // Strip ?tour=1 if present so refresh doesn't restart
    const params = new URLSearchParams(location.search);
    if (params.get("tour") === "1") {
      params.delete("tour");
      navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    }
  }, [user?.user_id, location.pathname, location.search, navigate, token, step]);

  const next = () => {
    // Record step completion BEFORE advancing
    track(token, `tour_step_completed_${step + 1}`, { step: step + 1, total_steps: STEPS.length });
    if (step >= STEPS.length - 1) { finish("completed"); return; }
    setStep(step + 1);
  };

  if (!active || !pos) return null;

  const current = STEPS[step];
  const { Icon } = current;

  return createPortal(
    <AnimatePresence>
      <motion.div
        key="onboarding-scrim"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9998] bg-black/40 backdrop-blur-[2px]"
        data-testid="onboarding-scrim"
        onClick={() => finish("skipped")}
      />
      <motion.div
        key={`onboarding-card-${current.id}`}
        initial={{ opacity: 0, y: 6, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 6, scale: 0.98 }}
        transition={{ duration: 0.18 }}
        role="dialog"
        aria-labelledby="onboarding-title"
        className="fixed z-[9999] rounded-2xl border border-amber-500/30 bg-white dark:bg-slate-900 shadow-2xl p-4 pr-9"
        style={{ top: pos.top, left: pos.left, width: pos.width }}
        data-testid="onboarding-tour-card"
      >
        <button
          type="button"
          aria-label="Skip tour"
          onClick={() => finish("skipped")}
          className="absolute top-2 right-2 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
          data-testid="onboarding-skip"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
            <Icon className="w-4 h-4 text-amber-600" />
          </div>
          <div className="text-[10px] uppercase tracking-wider text-amber-600 dark:text-amber-400">
            Step {step + 1} of {STEPS.length}
          </div>
        </div>

        <h4 id="onboarding-title" className="font-semibold text-sm text-slate-900 dark:text-slate-100">
          {current.title}
        </h4>
        <p className="text-xs text-slate-600 dark:text-slate-400 mt-1 leading-relaxed">
          {current.body}
        </p>

        <div className="flex items-center justify-between mt-3">
          <div className="flex gap-1.5">
            {STEPS.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === step ? "w-6 bg-amber-500" : "w-1.5 bg-slate-300 dark:bg-slate-700"
                }`}
              />
            ))}
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={() => finish(step === STEPS.length - 1 ? "completed" : "skipped")} className="h-7 text-xs" data-testid="onboarding-finish-btn">
              {step === STEPS.length - 1 ? "Done" : "Skip"}
            </Button>
            {step < STEPS.length - 1 && (
              <Button size="sm" onClick={next} className="h-7 gap-1 bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid="onboarding-next-btn">
                Next
                <ArrowRight className="w-3 h-3" />
              </Button>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>,
    document.body,
  );
}
